import React from 'react';
import { EntityBasicInfoForm } from '../../../components/shared/forms';
import { Business as BusinessIcon } from '@mui/icons-material';

/**
 * ClientBasicInfoForm - Client basic information form using the shared EntityBasicInfoForm
 * 
 * @param {Object} props - Component properties
 * @param {Object} props.data - Form data (replaces clientData)
 * @param {Function} props.onChange - Change handler function
 * @param {Function} props.setFieldValue - Field value setter function
 * @param {Object} props.errors - Validation errors
 * @param {Object} props.touched - Touched fields
 */
const ClientBasicInfoForm = ({ 
  data, 
  clientData, // For backward compatibility
  onChange,
  setFieldValue,
  errors = {}, 
  touched = {} 
}) => {
  // Support both new data prop and legacy clientData prop
  const formData = data || clientData || {};
  
  // Handle legacy onChange format
  const handleLegacyChange = (name, value) => {
    if (onChange) {
      // Check if it's the new onChange (event-based) or old onChange (name, value)
      if (typeof onChange === 'function' && onChange.length === 2) {
        onChange(name, value);
      } else if (setFieldValue) {
        setFieldValue(name, value);
      }
    }
  };
  
  return (
    <EntityBasicInfoForm
      data={formData}
      onChange={onChange}
      onCustomChange={handleLegacyChange}
      setFieldValue={setFieldValue}
      errors={errors}
      touched={touched}
      title="Basic Client Information"
      description="Enter the essential information about the client"
      icon={<BusinessIcon />}
      fields={[
        {
          name: 'name',
          label: 'Client Name',
          type: 'text',
          required: true,
          icon: 'business',
          gridSize: { xs: 12, sm: 6 },
          placeholder: "Enter client's company name"
        },
        {
          name: 'code',
          label: 'Client Code',
          type: 'text',
          icon: 'code',
          gridSize: { xs: 12, sm: 6 },
          placeholder: 'Auto-generated if left blank',
          helpText: 'A unique code for this client (optional)'
        },
        {
          name: 'industry',
          label: 'Industry',
          type: 'select',
          required: true,
          icon: 'category',
          gridSize: { xs: 12, sm: 6 },
          optionsKey: 'industry',
          placeholder: 'Select industry'
        },
        {
          name: 'region',
          label: 'Region',
          type: 'select',
          required: true,
          icon: 'location',
          gridSize: { xs: 12, sm: 6 },
          optionsKey: 'region',
          placeholder: 'Select region'
        },
        {
          name: 'description',
          label: 'Description',
          type: 'multiline',
          rows: 4,
          icon: 'info',
          gridSize: { xs: 12 },
          placeholder: 'Brief description of the client and their business'
        },
        {
          name: 'status',
          label: 'Status',
          type: 'select',
          required: true,
          gridSize: { xs: 12, sm: 6 },
          options: [
            { value: 'onboarding', label: 'Onboarding' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'paused', label: 'Paused' },
            { value: 'former', label: 'Former' }
          ],
          placeholder: 'Select client status'
        },
        {
          name: 'subscriptionTier',
          label: 'Subscription Tier',
          type: 'select',
          required: true,
          gridSize: { xs: 12, sm: 6 },
          options: [
            { value: 'basic', label: 'Basic' },
            { value: 'standard', label: 'Standard' },
            { value: 'premium', label: 'Premium' },
            { value: 'enterprise', label: 'Enterprise' },
            { value: 'custom', label: 'Custom' }
          ],
          helpText: 'Determines service levels and pricing',
          placeholder: 'Select subscription tier'
        }
      ]}
    />
  );
};

export default ClientBasicInfoForm;